import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { startSurfly } from '../../utilities/surfly';
import Colors from '../colors/colors';
import { BodyTextSmall } from '../typography/typography';
import type { StyleProps } from '../../utilities/types';
import { SynchronyInfo } from '../cms/blockText.constants';
import FooterInfo from './footer.constants';
import type { ReduxState } from '../../reducers';
import { clickTrack } from '../../analytics/clickTracking.constants';
import ImagesFileNames from '../../images';
import SVGImage from '../svgImage';
import footerStyles from './footer.styles';
import FooterLink from './footerLink';
import isFeatureFlagEnabled from '../../utilities/isFeatureFlagEnabled';

type FooterProps = {
  isLight?: boolean;
  isHalfScreen?: boolean;
};

type StateProps = {
  affinityCompanyCode?: string;
  isNAO?: boolean;
};

type Props = FooterProps & StyleProps & StateProps;

export const Footer = ({ isLight, isHalfScreen = false }: Props) => {
  const affinityCompanyCode = useSelector(
    (state: ReduxState) => state?.applications?.customerInfo?.affinityCompanyCode
  );
  const footerlinkProps = { isLight };
  const isCookieConsentEnabled = isFeatureFlagEnabled('COOKIE_CONSENT_FOOTER_ENABLED');

  return (
    <footer
      id="footer"
      className={isLight ? footerStyles.footerLight : footerStyles.footerDefault}
      role="contentinfo"
    >
      <div
        className={
          isHalfScreen
            ? footerStyles.footerLinksContainerHalf
            : footerStyles.footerLinksContainerFull
        }
        data-test={isHalfScreen ? 'footer-link-container-half' : 'footer-link-container-full'}
      >
        <ul
          className={
            isHalfScreen
              ? footerStyles.flexLinkContainerLeftHalf
              : footerStyles.flexLinkContainerLeftFull
          }
        >
          <FooterLink
            {...footerlinkProps}
            to={FooterInfo.TERMS_URL}
            data-test="footer-link-online-terms"
            data-track-title={clickTrack.footer.terms}
            affinityCompanyCode={affinityCompanyCode}
            title="Opens new window."
          >
            {FooterInfo.TERMS}
          </FooterLink>
          <FooterLink
            {...footerlinkProps}
            to={FooterInfo.ONLINE_PRIVACY_POLICY_URL}
            data-test="footer-link-privacy"
            data-track-title={clickTrack.footer.privacy}
            affinityCompanyCode={affinityCompanyCode}
            title="Opens new window."
          >
            {FooterInfo.ONLINE_PRIVACY_POLICY}
          </FooterLink>
          {isCookieConsentEnabled && (
            <FooterLink
              {...footerlinkProps}
              to={FooterInfo.CA_COOKIE_CONSENT_URL}
              data-test="footer-link-ca-cookie-consent"
              data-track-title={clickTrack.footer.ca_cookie_consent}
              affinityCompanyCode={affinityCompanyCode}
              title="Opens new window."
            >
              {FooterInfo.CA_COOKIE_CONSENT}
            </FooterLink>
          )}
          <FooterLink
            {...footerlinkProps}
            to={FooterInfo.DISCLOSURES_URL}
            data-test="footer-link-disclosures"
            data-track-title={clickTrack.footer.disclosures}
            affinityCompanyCode={affinityCompanyCode}
            title="Opens new window."
          >
            {FooterInfo.DISCLOSURES}
          </FooterLink>
          <FooterLink
            {...footerlinkProps}
            to={FooterInfo.ACCOUNT_PRIVACY_POLICY_URL}
            data-test="footer-link-account-privacy"
            data-track-title={clickTrack.footer.privacy_policy}
            affinityCompanyCode={affinityCompanyCode}
            title="Opens new window."
          >
            {FooterInfo.ACCOUNT_PRIVACY_POLICY}
          </FooterLink>
          {window.Surfly && window.__config__.SURFLY_ENABLED && (
            <FooterLink
              {...footerlinkProps}
              data-test="footer-link-co-browse"
              data-track-title={clickTrack.footer.coBrowse}
              href="#"
              onClick={(event) => {
                event.preventDefault();
                startSurfly();
              }}
              rel="nofollow"
              role="button"
              title="Start Cobrowse session"
            >
              {FooterInfo.COBROWSE}
            </FooterLink>
          )}
        </ul>
        <ul
          className={
            isHalfScreen
              ? footerStyles.flexLinkContainerRightHalf
              : footerStyles.flexLinkContainerRightFull
          }
        >
          <FooterLink
            {...footerlinkProps}
            data-test="footer-link-contact-us"
            to={FooterInfo.CONTACT_US_URL}
            data-track-title={clickTrack.footer.contact}
            affinityCompanyCode={affinityCompanyCode}
            title="Opens new window."
          >
            {FooterInfo.CONTACT_US}
          </FooterLink>
          <FooterLink
            {...footerlinkProps}
            to={FooterInfo.FAQ_URL}
            data-test="footer-link-faq"
            data-track-title={clickTrack.footer.faq}
            affinityCompanyCode={affinityCompanyCode}
            title="Opens new window."
          >
            {FooterInfo.FAQ}
          </FooterLink>
        </ul>
      </div>
      <BodyTextSmall textColor={isLight ? Colors.footerGray : Colors.white}>
        &copy; {moment().year()} {SynchronyInfo.BANKNAME}. All Rights Reserved.
      </BodyTextSmall>
      <div className={footerStyles.fdicWrapper}>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={
            affinityCompanyCode
              ? `${FooterInfo.FDIC_URL}?UISCode=${affinityCompanyCode}`
              : FooterInfo.FDIC_URL
          }
          aria-label="Member FDIC"
          title="Opens new window."
          data-test="footer-link-fdic"
          data-track-title={clickTrack.footer.fdic}
        >
          <SVGImage
            imageName={ImagesFileNames.memberFdicSvg}
            ariaHidden="true"
            fill={isLight ? Colors.footerGray : Colors.white}
          />
        </a>
      </div>
    </footer>
  );
};

export default Footer;
